/**
 * 連打間隔エディタ（バレル）。
 *
 * 縦軸 = 音価（上 1/64 〜 下 1/2、log2 等間隔の 6 段）、
 * 横軸 = 連打フレーズ内の時間（連打長さで正規化）。
 * 左右のハンドルを縦ドラッグして開始/終了の間隔を音価スナップで指定する
 * （同じ高さ = 固定、違う高さ = 直線、CURVE ノブで曲線化）。
 * buildStutterPlan の実ヒットを弾丸ドットとして重ね、量子化・アクセルの
 * 効きがそのまま見える。試聴中は再生ヘッドの白線を表示する。
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { RENDA_COLORS, RENDA_FONT, RENDA_PANEL } from "../palette";
import {
  INTERVAL_STEPS,
  STEP_MAX,
  STEP_MIN,
  phraseLengthSec,
  stepCurveAt,
  type RendaParams,
  type StutterHit,
} from "../utils/stutterPlan";

const H = 190;
const PAD_L = 44;
const PAD_R = 14;
const PAD_T = 10;
const PAD_B = 10;
/** スクリーン枠から描画コンテンツまでの内側余白 */
const INSET_X = 14;
const INSET_Y = 14;
const CURVE_SAMPLES = 64;

const Wrapper = styled.div`
  width: 100%;
`;

const EditorSvg = styled.svg`
  display: block;
  touch-action: none;
`;

const HandleCircle = styled.circle`
  cursor: ns-resize;
  outline: none;

  &:focus-visible {
    filter: drop-shadow(0 0 5px ${RENDA_PANEL.focus});
  }
`;

export interface IntervalEditorProps {
  params: RendaParams;
  hits: StutterHit[];
  /** フレーズ先頭からの再生位置（秒）。範囲外・停止中は null */
  playheadPhraseSec: number | null;
  onChangeStartStep: (step: number) => void;
  onChangeEndStep: (step: number) => void;
}

const IntervalEditor: React.FC<IntervalEditorProps> = ({
  params,
  hits,
  playheadPhraseSec,
  onChangeStartStep,
  onChangeEndStep,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [width, setWidth] = useState(0);
  const draggingRef = useRef<"start" | "end" | null>(null);

  const paramsRef = useRef(params);
  paramsRef.current = params;
  const onChangeStartRef = useRef(onChangeStartStep);
  onChangeStartRef.current = onChangeStartStep;
  const onChangeEndRef = useRef(onChangeEndStep);
  onChangeEndRef.current = onChangeEndStep;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setWidth(Math.floor(entries[0]?.contentRect.width ?? 0));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ---- ジオメトリ ----
  const screenX = PAD_L;
  const screenY = PAD_T;
  const screenW = Math.max(0, width - PAD_L - PAD_R);
  const screenH = H - PAD_T - PAD_B;
  const cx0 = screenX + INSET_X;
  const cx1 = screenX + screenW - INSET_X;
  const cy0 = screenY + INSET_Y;
  const cy1 = screenY + screenH - INSET_Y;

  const yForStep = useCallback(
    (step: number): number =>
      cy0 + ((step - STEP_MIN) / (STEP_MAX - STEP_MIN)) * (cy1 - cy0),
    [cy0, cy1],
  );
  const xForP = (p: number): number => cx0 + p * (cx1 - cx0);

  const stepFromClientY = useCallback(
    (clientY: number): number => {
      const svg = svgRef.current;
      if (!svg) return STEP_MIN;
      const rect = svg.getBoundingClientRect();
      const frac = (clientY - rect.top - cy0) / Math.max(1, cy1 - cy0);
      const step = Math.round(STEP_MIN + frac * (STEP_MAX - STEP_MIN));
      return Math.min(STEP_MAX, Math.max(STEP_MIN, step));
    },
    [cy0, cy1],
  );

  // ---- ハンドル操作 ----
  const applyHandleStep = useCallback(
    (handle: "start" | "end", step: number) => {
      const current = paramsRef.current;
      if (handle === "start") {
        if (step !== current.intervalStartStep) onChangeStartRef.current(step);
      } else if (step !== current.intervalEndStep) {
        onChangeEndRef.current(step);
      }
    },
    [],
  );

  const handlePointerDown = useCallback(
    (handle: "start" | "end") => (e: React.PointerEvent<SVGCircleElement>) => {
      e.preventDefault();
      (e.target as Element).setPointerCapture?.(e.pointerId);
      draggingRef.current = handle;
      applyHandleStep(handle, stepFromClientY(e.clientY));
    },
    [applyHandleStep, stepFromClientY],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGCircleElement>) => {
      const handle = draggingRef.current;
      if (!handle) return;
      applyHandleStep(handle, stepFromClientY(e.clientY));
    },
    [applyHandleStep, stepFromClientY],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<SVGCircleElement>) => {
      draggingRef.current = null;
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (handle: "start" | "end") => (e: React.KeyboardEvent<SVGCircleElement>) => {
      // 画面上の上方向 = 速い音価（step 小）に合わせる
      let delta = 0;
      if (e.key === "ArrowUp") delta = -1;
      if (e.key === "ArrowDown") delta = 1;
      if (delta === 0) return;
      e.preventDefault();
      const current =
        handle === "start"
          ? paramsRef.current.intervalStartStep
          : paramsRef.current.intervalEndStep;
      const next = Math.min(STEP_MAX, Math.max(STEP_MIN, current + delta));
      applyHandleStep(handle, next);
    },
    [applyHandleStep],
  );

  if (width < 80) {
    return <Wrapper ref={wrapperRef} style={{ height: H }} />;
  }

  const lenSec = phraseLengthSec(params.lengthSixteenths, params.bpm);
  const columns = params.lengthSixteenths;

  // カーブ線（アクセル・量子化を含まない生カーブ）
  const curvePoints = Array.from({ length: CURVE_SAMPLES + 1 }, (_, i) => {
    const p = i / CURVE_SAMPLES;
    return `${xForP(p)},${yForStep(stepCurveAt(params, p))}`;
  }).join(" ");

  const dotRadius = hits.length > 120 ? 2.5 : 4;
  const playheadP =
    playheadPhraseSec !== null && lenSec > 0
      ? playheadPhraseSec / lenSec
      : null;

  const renderHandle = (handle: "start" | "end") => {
    const step =
      handle === "start" ? params.intervalStartStep : params.intervalEndStep;
    const cx = handle === "start" ? cx0 : cx1;
    const color =
      handle === "start" ? RENDA_COLORS.soremoBlue : RENDA_COLORS.soremoGreen;
    const label = handle === "start" ? "連打間隔（開始）" : "連打間隔（終了）";
    return (
      <g>
        <HandleCircle
          cx={cx}
          cy={yForStep(step)}
          r={10}
          fill={color}
          stroke={RENDA_PANEL.outline}
          strokeWidth={3}
          tabIndex={0}
          role="slider"
          aria-label={label}
          aria-valuemin={STEP_MIN}
          aria-valuemax={STEP_MAX}
          aria-valuenow={step}
          aria-valuetext={INTERVAL_STEPS[step]?.label ?? String(step)}
          onPointerDown={handlePointerDown(handle)}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown(handle)}
        />
        <circle
          cx={cx - 3}
          cy={yForStep(step) - 3}
          r={3}
          fill="rgba(255,255,255,0.55)"
          pointerEvents="none"
        />
      </g>
    );
  };

  return (
    <Wrapper ref={wrapperRef}>
      <EditorSvg
        ref={svgRef}
        width={width}
        height={H}
        viewBox={`0 0 ${width} ${H}`}
      >
        {/* スクリーン（LCD 風の暗色面） */}
        <rect
          x={screenX}
          y={screenY}
          width={screenW}
          height={screenH}
          rx={12}
          fill={RENDA_PANEL.displayBg}
          stroke={RENDA_PANEL.outline}
          strokeWidth={3}
        />

        {/* 縦グリッド（16分、拍は強調） */}
        {Array.from({ length: columns + 1 }, (_, i) => {
          const x = xForP(i / columns);
          const isBeat = i % 4 === 0;
          return (
            <line
              key={`v${i}`}
              x1={x}
              y1={cy0 - 6}
              x2={x}
              y2={cy1 + 6}
              stroke={
                isBeat ? "rgba(238,247,255,0.28)" : "rgba(238,247,255,0.1)"
              }
              strokeWidth={isBeat ? 1.5 : 1}
            />
          );
        })}

        {/* 横グリッド + 音価ラベル */}
        {INTERVAL_STEPS.map(({ step, label }) => {
          const y = yForStep(step);
          return (
            <g key={label}>
              <line
                x1={cx0 - 8}
                y1={y}
                x2={cx1 + 8}
                y2={y}
                stroke="rgba(238,247,255,0.12)"
                strokeWidth={1}
              />
              <text
                x={screenX - 8}
                y={y + 3.5}
                textAnchor="end"
                fontFamily={RENDA_FONT}
                fontWeight={800}
                fontSize={10}
                fill={RENDA_PANEL.engraving}
              >
                {label}
              </text>
            </g>
          );
        })}

        {/* 間隔カーブ（インクピンク） */}
        <polyline
          points={curvePoints}
          fill="none"
          stroke={RENDA_COLORS.inkPink}
          strokeWidth={3}
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={0.9}
        />

        {/* 実ヒット（弾丸ドット。量子化・アクセル込みの実効値） */}
        {hits.map((hit, i) => {
          const p = lenSec > 0 ? hit.timeSec / lenSec : 0;
          const displayStep = Math.min(STEP_MAX, Math.max(STEP_MIN, hit.step));
          return (
            <circle
              key={i}
              cx={xForP(p)}
              cy={yForStep(displayStep)}
              r={dotRadius}
              fill={RENDA_COLORS.inkYellow}
              stroke={RENDA_PANEL.outline}
              strokeWidth={1.2}
            />
          );
        })}

        {/* 再生ヘッド */}
        {playheadP !== null && playheadP >= 0 && playheadP <= 1 && (
          <line
            x1={xForP(playheadP)}
            y1={cy0 - 8}
            x2={xForP(playheadP)}
            y2={cy1 + 8}
            stroke="rgba(255,255,255,0.9)"
            strokeWidth={2}
          />
        )}

        {renderHandle("start")}
        {renderHandle("end")}
      </EditorSvg>
    </Wrapper>
  );
};

export default IntervalEditor;
